import { Form } from "@shared/antd/form";
import { Modal } from "@shared/antd/modal";
import React, { createElement, Fragment } from "react";
import { TextArea } from "app/shared/antd/text-area";
import { FormattedMessage, useIntl } from "react-intl";
import { FormComponentProps } from "antd/lib/form";
import { BprocFormOutcome } from "../../../../cuba/entities/bproc/bproc_FormOutcome";
import { NotPersisitBprocActors } from "cuba/entities/base/tsadv_NotPersisitBprocActors";
import { useApplicationStore } from "app/entities/form-controller/abstract";
import { ApproverTable } from "app/features/approver-table/approver-table";
import { ColumnProps } from "antd/lib/table";
import { SerializedEntity } from "@cuba-platform/rest";
import Candidate from "app/pages/Bproc/component/Candidate";
import { TsadvUser } from "cuba/entities/base/tsadv$UserExt";
import { CustomSelectedActors } from "app/pages/Bproc/modal/StartBprocModal";
import { useNextApprovers } from "app/features/bpm-controller/model/useGetNextApprover";

interface AdditionalApproverModalProps extends FormComponentProps {
  outcome: BprocFormOutcome;
  title: string;
  loading: boolean;
  modalVisibleMap: Map<string, boolean>;
  handleOk: (outcome: BprocFormOutcome) => void;
  handleCancel: (outcome: BprocFormOutcome) => void;
  idText: string;
  commentRequiredOutcomes?: string[];
  openedOutcomeModal: string;
  commentValidator: (rule: any, value: any, callback: any) => void;
  updateCommentText: (value: string) => void;
  customSelectedActors: CustomSelectedActors[];
  setCustomSelectedActors: React.Dispatch<
    React.SetStateAction<CustomSelectedActors[]>
  >;
}

const SEND_FOR_APPROVAL = "SEND_FOR_APPROVAL";
const APPROVE = "APPROVE";

export const AdditionalApproverModal = (
  props: AdditionalApproverModalProps,
) => {
  const {
    outcome,
    title,
    loading,
    modalVisibleMap,
    handleOk,
    handleCancel,
    form,
    idText,
    commentRequiredOutcomes,
    openedOutcomeModal,
    commentValidator,
    updateCommentText,
    customSelectedActors,
    setCustomSelectedActors,
  } = props;
  const intl = useIntl();
  const store = useApplicationStore();

  const { dataInstance, bpmStore } = store();
  const { activeTask } = bpmStore();
  const { data: allActors, isFetching } = useNextApprovers({
    request: dataInstance,
    currentTaskCode: activeTask?.name,
    outcome,
    SEND_FOR_APPROVAL,
    APPROVE,
  });

  const isNeedSelectedTable =
    outcome.id === APPROVE || outcome.id === SEND_FOR_APPROVAL;

  const changeCustomSelectedActors = (
    currentTaskCode: string,
    selectedActors: TsadvUser[],
  ) => {
    setCustomSelectedActors((prev) => {
      const exists = prev.some(
        (item) => item.currentTaskCode === currentTaskCode,
      );

      if (exists) {
        return prev.map((item) =>
          item.currentTaskCode === currentTaskCode
            ? { ...item, selectedApprovers: selectedActors }
            : item,
        );
      } else {
        return [
          ...prev,
          { currentTaskCode, selectedApprovers: selectedActors },
        ];
      }
    });
  };

  const columns = React.useMemo(
    (): ColumnProps<SerializedEntity<NotPersisitBprocActors>>[] => [
      {
        key: "role",
        title: <FormattedMessage id="bproc.startBproc.modal.roles" />,
        render: (_, record) => record?.hrRole?._instanceName,
      },
      {
        key: "candidates",
        title: <FormattedMessage id="bproc.startBproc.modal.users" />,
        render: (_, record, index) => (
          <Candidate
            candidates={record?.users || []}
            isCanBeSelected={index === 0}
            currentTaskCode={record?.hrRole?.code}
            changeCustomSelectedActors={changeCustomSelectedActors}
            customSelectedActors={customSelectedActors}
          />
        ),
      },
    ],
    [customSelectedActors],
  );
  return (
    <Modal
      title={title}
      confirmLoading={loading}
      visible={modalVisibleMap.get(outcome?.id)}
      onOk={handleOk.bind(null, outcome)}
      onCancel={handleCancel.bind(null, outcome)}
    >
      {isNeedSelectedTable && (
        <div className={"mb-4"}>
          <ApproverTable
            items={allActors}
            columns={columns}
            loading={isFetching}
          />
        </div>
      )}

      <Form.Item
        label={createElement(
          Fragment,
          null,
          intl.formatMessage({ id: "comment" }),
        )}
      >
        {form.getFieldDecorator(idText, {
          rules: [
            {
              required: !!(commentRequiredOutcomes || []).find(
                (o) => o === openedOutcomeModal,
              ),
              validator: commentValidator,
            },
          ],
          getValueFromEvent: (args) => {
            updateCommentText(args.target.value);
            return args.target.value;
          },
        })(<TextArea rows={4} />)}
      </Form.Item>
    </Modal>
  );
};
